import { useCallback, useEffect, useRef, useState } from "react"
import { useAuthStore } from "@/features/auth/store/auth.store"
import {
  lookupSelfContact,
  registerSelfContact,
} from "@/sdk/instavcMessenger-instance"
import { readClaimedOrgName, readContactOrgName } from "../services/contact-org"
import { reconcileStoredPresenceId } from "./stored-presence"
import {
  REGISTER_MAX_ATTEMPTS,
  backoffMs,
  retryableFailure,
  statusOf,
} from "../services/contact-registration"

/**
 * Gates the chat tab behind a one-time terms-and-conditions step.
 *
 * On first mount (per authenticated user) it asks the SDK server whether the
 * current user already has a tenant contact/identity (`GET /contacts/self`).
 * If not, `status` becomes `needs-terms` and the caller shows the T&C gate;
 * `accept()` registers the contact (`POST /contacts/self`) — which mints/returns
 * the tenant-shared `contactPresenceId` — and flips `status` to `ready`.
 *
 * Everything runs over the SDK's REST path (no socket), tenant + email are taken
 * from the app JWT server-side, so this hook needs neither.
 */

const TERMS_VERSION = "1.0"
/** Lookup retry budget — 1s, 2s, 4s, 8s, 16s. A cold start races the SDK token
 *  mint, and a single failed lookup used to leave `presenceId` null for the whole
 *  session (no chat subscriptions, no presence). */
/** userId -> the stored presence id already checked with the server on this
 *  page load. Module-level because the hook mounts in several places at once. */
const verifiedPresence = new Map<string, string>()
/** Users who chose "Maybe later" on this page load. Module-level, like
 *  `verifiedPresence`: the chat page unmounts whenever the user goes elsewhere,
 *  and a dismissal held in its state was forgotten — the terms came back by
 *  themselves on every return to Chats. */
const declinedTerms = new Set<string>()
const LOOKUP_MAX_ATTEMPTS = 5
const LOOKUP_RETRY_BASE_MS = 1000

export type ContactGateStatus =
  | "checking"
  | "needs-terms"
  | "registering"
  | "ready"
  | "error"

export interface ContactRegistration {
  status: ContactGateStatus
  /** True when the (dismissible) T&C gate should be shown in the chat UI. */
  gated: boolean
  accept: () => Promise<void>
  /** Dismiss the gate ("Maybe later") — chat stays inactive until accepted. */
  dismiss: () => void
  /** Chat still needs consent and the user said "Maybe later". */
  declined: boolean
  /** Offer the terms again — the user tried to use chat after "Maybe later". */
  reopen: () => void
  retry: () => void
}

export function useContactRegistration(): ContactRegistration {
  const myUserId = useAuthStore((s) => s.user?.id)
  const setPresenceId = useAuthStore((s) => s.setPresenceId)
  const knownPresenceId = useAuthStore((s) => s.presenceId)
  const [status, setStatus] = useState<ContactGateStatus>("checking")
  const [dismissedHere, setDismissed] = useState(false)
  /**
   * Still mounted?
   *
   * `accept` now sleeps between attempts, so it can outlive the surface that
   * called it — the user closes the chat panel mid-backoff and a later
   * `setStatus` would warn about updating an unmounted component and, worse,
   * resurrect a gate they had dismissed.
   */
  const aliveRef = useRef(true)
  useEffect(() => {
    aliveRef.current = true
    return () => {
      aliveRef.current = false
    }
  }, [])

  /**
   * Repair a stored company name that has gone stale.
   *
   * `contactOrgName` is written once, at the terms gate — and that gate never
   * shows again, so a contact registered while the name was the hardcoded
   * 'inmeet' keeps it for ever even though the token has said otherwise ever
   * since. The upsert behind `registerSelf` already overwrites the column
   * (`contact_org_name = EXCLUDED.contact_org_name`) and deliberately keeps the
   * EARLIEST `terms_accepted_at`, so calling it again refreshes the label
   * without re-consenting or rewriting when consent was given.
   *
   * Only ever fires when the token genuinely names an organisation. Falling
   * back here would overwrite a correct stored name with 'inmeet' for any user
   * whose token happens not to carry one — turning a one-off bug into a
   * recurring one.
   *
   * Once per mount, and failure is deliberately soft: this is a cosmetic
   * repair, and the user's chat identity already exists.
   */
  const refreshedRef = useRef(false)
  const refreshOrgName = useCallback(
    (stored: string | null | undefined) => {
      if (!myUserId || refreshedRef.current) return
      const claimed = readClaimedOrgName()
      if (!claimed || claimed === stored) return

      refreshedRef.current = true
      registerSelfContact(myUserId, { termsVersion: TERMS_VERSION, contactOrgName: claimed })
        .then(() => {
          console.info('[contacts] refreshed a stale organisation name', { from: stored, to: claimed })
        })
        .catch((err: unknown) => {
          console.warn('[contacts] could not refresh the organisation name', {
            from: stored,
            to: claimed,
            message: err instanceof Error ? err.message : String(err),
          })
        })
    },
    [myUserId],
  )

  const check = useCallback(() => {
    if (!myUserId) return
    // A presenceId already in the store (restored from the persisted session, or
    // resolved by another mount of this hook; it runs in ProtectedRoute AND in
    // the chat surfaces) is used straight away, so chat starts on the first
    // render. It is still checked against the server once per page load: a
    // saved id that went stale was trusted for good, and every chat then
    // treated this user as a stranger.
    if (knownPresenceId) {
      setStatus("ready")
      if (verifiedPresence.get(myUserId) === knownPresenceId) return
      verifiedPresence.set(myUserId, knownPresenceId)
      lookupSelfContact(myUserId)
        .then((res) => {
          const next = reconcileStoredPresenceId(knownPresenceId, res)
          if (next === undefined) return
          console.info('[contacts] replaced a stale presence id restored from storage', { cleared: next === null })
          if (next) verifiedPresence.set(myUserId, next)
          else verifiedPresence.delete(myUserId)
          // Not cancelled on unmount: the store outlives this mount, and the
          // check is not repeated for this id, so dropping the answer would
          // leave the stale id in place.
          setPresenceId(next)
        })
        .catch(() => {
          // Keep the saved id; a later mount checks again.
          verifiedPresence.delete(myUserId)
        })
      return
    }
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null

    // Retries on transient failure. This lookup is what publishes `presenceId`,
    // and EVERYTHING in chat/presence is gated on it — the owner channel (own
    // presence + new-conversation events) and every live message subscription. A
    // single network blip here used to mean "silently no chat and permanently
    // offline" until the user reloaded.
    const attempt = (n: number) => {
      setStatus("checking")
      lookupSelfContact(myUserId)
        .then((res) => {
          if (cancelled) return
          // Publish the per-org identity so the chat/presence layer can start
          // (they read `useAuthStore(s => s.presenceId)`). Left null when the
          // contact doesn't exist yet — the terms gate resolves it on accept.
          if (res.exists && res.contact?.contactPresenceId) {
            setPresenceId(res.contact.contactPresenceId)
            refreshOrgName(res.contact.contactOrgName)
          }
          setStatus(res.exists ? "ready" : "needs-terms")
        })
        .catch(() => {
          if (cancelled) return
          if (n + 1 >= LOOKUP_MAX_ATTEMPTS) {
            setStatus("error")
            return
          }
          timer = setTimeout(() => attempt(n + 1), LOOKUP_RETRY_BASE_MS * 2 ** n)
        })
    }
    attempt(0)

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [myUserId, setPresenceId, knownPresenceId, refreshOrgName])

  useEffect(() => {
    const cleanup = check()
    return cleanup
  }, [check])

  /**
   * Register the contact, retrying the failures worth retrying.
   *
   * This call is the user's entire existence in the directory: it creates the
   * `sdk_contacts` row that mints `contactPresenceId` and that contact SEARCH
   * reads. Without it they cannot be found by anyone at any scope — not their
   * sub-org, not their org, not global — however well the rest of the app
   * works for them.
   *
   * It used to be one attempt wrapped in a bare `catch {}`. A single dropped
   * POST therefore cost a user their directory presence for the life of the
   * tab, with nothing logged, nothing retried, and `retry` re-running only the
   * LOOKUP — which answers `exists: false` and returns them to the gate they
   * just pressed. The lookup above has retried five times with backoff for
   * exactly this reason since someone lost chat to a network blip; this is the
   * same budget, applied to the half that actually creates the record.
   */
  const accept = useCallback(async () => {
    if (!myUserId) return
    setStatus("registering")

    for (let attempt = 0; attempt < REGISTER_MAX_ATTEMPTS; attempt++) {
      try {
        // contactOrgName is app-provided and mandatory server-side. It was the
        // literal 'inmeet' for every user of every tenant, which collapsed
        // every organisation into one label — so the field that exists to tell
        // apart people from different companies could not. Read from the
        // signed-in user's own token now, falling back to 'inmeet' when the
        // token names no organisation, which is still the common case.
        const contact = await registerSelfContact(myUserId, {
          termsVersion: TERMS_VERSION,
          contactOrgName: readContactOrgName(),
        })
        if (!aliveRef.current) return
        // Registration mints/returns the contactPresenceId — publish it so chat
        // + presence subscriptions unblock immediately after consent.
        if (contact?.contactPresenceId) setPresenceId(contact.contactPresenceId)
        setStatus("ready")
        return
      } catch (err) {
        if (!aliveRef.current) return

        const last = attempt + 1 >= REGISTER_MAX_ATTEMPTS
        if (!retryableFailure(err) || last) {
          // Ungated, like the [lastN] / [grid] / [activeSpeaker] lines, and for
          // the same reason: the question "why can nobody find me" is asked
          // about a deployed session, where a debug flag cannot be set after
          // the fact. The old bare `catch {}` is precisely why this failure had
          // no symptom other than absence.
          console.error('[contacts] registration failed — this user will not appear in search', {
            status: statusOf(err) ?? 'no response',
            retryable: retryableFailure(err),
            attempts: attempt + 1,
            message: err instanceof Error ? err.message : String(err),
          })
          setStatus("error")
          return
        }

        const wait = backoffMs(attempt)
        console.warn('[contacts] registration attempt failed, retrying', {
          status: statusOf(err) ?? 'no response',
          attempt: attempt + 1,
          retryInMs: wait,
        })
        await new Promise((resolve) => setTimeout(resolve, wait))
        if (!aliveRef.current) return
      }
    }
  }, [myUserId, setPresenceId])

  const dismiss = useCallback(() => {
    if (myUserId) declinedTerms.add(myUserId)
    setDismissed(true)
  }, [myUserId])

  const reopen = useCallback(() => {
    if (myUserId) declinedTerms.delete(myUserId)
    setDismissed(false)
  }, [myUserId])

  // Said here, or on an earlier visit to the page this load.
  const dismissed = dismissedHere || (!!myUserId && declinedTerms.has(myUserId))

  return {
    status,
    // Show the (dismissible) gate only while consent is still needed and the
    // user hasn't dismissed it this session. Lookup errors fail OPEN.
    gated: (status === "needs-terms" || status === "registering") && !dismissed,
    accept,
    dismiss,
    declined: status === "needs-terms" && dismissed,
    reopen,
    retry: check,
  }
}
